import { useEffect, useMemo, useState } from "react"
import {
    Area,
    Bar,
    CartesianGrid,
    Cell,
    ComposedChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts"
import api from "../api"
import { CourseType } from "../common/constants"
import Card from "../components/cards/Card"
import MultipleSelect from "../components/MultipleSelect"
import Skeleton from "../components/Skeleton"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import Breadcrumb from "../components/Breadcrumb"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import { Helmet, HelmetProvider } from "react-helmet-async"
import styles from "./TeachingReports.module.css"

const PIE_COLORS = ["#26aa87", "#d4a54e", "#dc7880"]
const number = (value) => Number(value || 0).toLocaleString("it-IT")
const dateLabel = (value) => new Date(`${value}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "2-digit" })

const Distribution = ({ items, loading }) => loading ? <Skeleton width="100%" height={160} /> : !items.some(item => item.count > 0) ? <div className={styles.empty}>Nessun dato disponibile</div> : (
    <div className={styles.distribution}>
        {items.map((item) => <div className={styles.distributionRow} key={item.label}>
            <div><span>{item.label}</span><strong>{number(item.count)} <small>· {formatPercent(item.percentage)}</small></strong></div>
            <div className={styles.track}><span style={{ width: `${Math.min(100, Math.max(0, item.percentage))}%`, background: item.color || "var(--primary)" }} /></div>
        </div>)}
    </div>
)

const mapOptions = (items = []) => {
    return (items || []).map((item) => ({ id: item.id, name: item.name }))
}

const normalizeName = (value) => (value || "").toString().trim().toLowerCase()

const sortByName = (items = []) => {
    return [...(items || [])].sort((a, b) => normalizeName(a?.name).localeCompare(normalizeName(b?.name), "it"))
}

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`

const formatDemographicLabel = (label) => {
    const value = String(label || "").trim().toLowerCase()
    if (!value) return "N/D"

    const labels = {
        male: "Uomo",
        female: "Donna",
        other: "Altro",
        "non disponibile": "Non disponibile",
        studente: "Studente",
        lavoratore: "Lavoratore",
        "studente/lavoratore": "Studente/Lavoratore",
        nessuno: "Nessuno",
    }

    return labels[value] || label
}

const formatDurationHm = (totalMinutes) => {
    const safeMinutes = Math.max(Math.round(Number(totalMinutes || 0)), 0)
    const hours = Math.floor(safeMinutes / 60)
    const minutes = safeMinutes % 60
    return `${hours}h ${minutes}m`
}

const uniqueById = (items = []) => {
    const dedupe = new Map()
    for (const item of items) {
        if (!item || item.id == null) {
            continue
        }
        dedupe.set(item.id, item)
    }
    return Array.from(dedupe.values())
}

const buildDashboardSectionPath = (filters, section) => {
    const params = new URLSearchParams()
    if (filters.courses.length > 0) {
        params.set("course_ids", filters.courses.map((item) => item.id).join(","))
    }
    if (filters.editions.length > 0) {
        params.set("edition_ids", filters.editions.map((item) => item.id).join(","))
    }
    params.set("sections", section)
    const query = params.toString()
    return `/admin/reports/teachings/dashboard${query ? `?${query}` : ""}`
}

const TeachingReports = () => {
    const [filters, setFilters] = useState({
        courses: [],
        editions: [],
    })
    const [options, setOptions] = useState({
        courses: [],
        editions: [],
    })
    const [loading, setLoading] = useState({
        courses: false,
        editions: false,
        reportSummary: false,
        reportBreakdown: false,
        reportLearning: false,
        reportTeachers: false,
    })
    const [trendMode, setTrendMode] = useState("cumulative")
    const [errors, setErrors] = useState({})
    const [report, setReport] = useState({})
    const [expandedCourseIds, setExpandedCourseIds] = useState([])
    const [expandedTeacherIds, setExpandedTeacherIds] = useState([])
    const [courseSearch, setCourseSearch] = useState("")
    const [editionSearch, setEditionSearch] = useState("")
    const [enrollmentsTableSearch, setEnrollmentsTableSearch] = useState("")
    const [teachersTableSearch, setTeachersTableSearch] = useState("")

    useEffect(() => {
        let active = true

        const loadCourses = async () => {
            setLoading((current) => ({ ...current, courses: true }))
            try {
                const courses = await api.get("/admin/courses?complete=false")
                if (!active) {
                    return
                }

                const filteredCourses = (courses || [])
                    .filter((course) => !course?.archived)
                    .filter((course) => String(course?.type) !== String(CourseType.Webinar))

                setOptions((current) => ({ ...current, courses: sortByName(mapOptions(filteredCourses)) }))
            } catch (error) {
                console.error(error)
            }
            if (active) {
                setLoading((current) => ({ ...current, courses: false }))
            }
        }

        loadCourses()

        return () => {
            active = false
        }
    }, [])

    useEffect(() => {
        let active = true

        const loadEditions = async () => {
            if (filters.courses.length === 0) {
                setOptions((current) => ({ ...current, editions: [] }))
                setFilters((current) => ({ ...current, editions: [] }))
                return
            }

            setLoading((current) => ({ ...current, editions: true }))
            try {
                const responses = await Promise.all(
                    filters.courses.map((course) => api.get(`/admin/courses/${course.id}/editions`))
                )

                if (!active) {
                    return
                }

                const editions = uniqueById(responses.flat())
                setOptions((current) => ({ ...current, editions: sortByName(mapOptions(editions)) }))
                setFilters((current) => ({ ...current, editions: [] }))
                setEditionSearch("")
            } catch (error) {
                console.error(error)
            }

            if (active) {
                setLoading((current) => ({ ...current, editions: false }))
            }
        }

        loadEditions()

        return () => {
            active = false
        }
    }, [filters.courses])

    useEffect(() => {
        let active = true

        const loadSection = async (section, loadingKey) => {
            setLoading((current) => ({ ...current, [loadingKey]: true }))
            try {
                const path = buildDashboardSectionPath(filters, section)
                const response = await api.get(path, { timeout: 0 })
                if (active) {
                    setReport((current) => ({ ...current, ...response }))
                }
            } catch (error) {
                console.error(error)
                if (active) setErrors(current => ({ ...current, [loadingKey]: true }))
            }
            if (active) {
                setLoading((current) => ({ ...current, [loadingKey]: false }))
            }
        }

        setReport({})
        setErrors({})
        loadSection("summary", "reportSummary")
        loadSection("enrollments_breakdown", "reportBreakdown")
        loadSection("learning_metrics", "reportLearning")
        loadSection("teachers_hours", "reportTeachers")

        return () => {
            active = false
        }
    }, [filters])

    const segmentChartData = useMemo(() => {
        const segments = report?.student_segments || {}
        return [
            { name: "Attivi", value: segments.active || 0 },
            { name: "Passivi", value: segments.passive || 0 },
            { name: "A rischio", value: segments.at_risk || 0 },
        ]
    }, [report])

    const typeChartData = useMemo(() => {
        const distribution = report?.course_type_distribution || {}
        return [
            { name: "Master", value: distribution.master || 0 },
            { name: "Masterclass", value: distribution.masterclass || 0 },
            { name: "Edufin", value: distribution.edufin || 0 },
        ]
    }, [report])

    const studentDemographics = useMemo(() => {
        return report?.student_demographics || { summary: {}, age: [], gender: [], employment: [] }
    }, [report])

    const studentDemographicsSummary = useMemo(() => {
        return studentDemographics?.summary || { avg_age: null, students_total: 0 }
    }, [studentDemographics])

    const ageDistributionChartData = useMemo(() => {
        return (Array.isArray(studentDemographics?.age) ? studentDemographics.age : []).map((item) => ({
            label: formatDemographicLabel(item?.label),
            percentage: Number(item?.percentage || 0),
            count: Number(item?.count || 0),
        }))
    }, [studentDemographics])

    const genderDistributionChartData = useMemo(() => {
        return (Array.isArray(studentDemographics?.gender) ? studentDemographics.gender : []).map((item) => ({
            label: formatDemographicLabel(item?.label),
            percentage: Number(item?.percentage || 0),
            count: Number(item?.count || 0),
        }))
    }, [studentDemographics])

    const employmentDistributionChartData = useMemo(() => {
        return (Array.isArray(studentDemographics?.employment) ? studentDemographics.employment : []).map((item) => ({
            label: formatDemographicLabel(item?.label),
            percentage: Number(item?.percentage || 0),
            count: Number(item?.count || 0),
        }))
    }, [studentDemographics])

    const trendData = useMemo(() => {
        return report?.enrollment_trend || []
    }, [report])

    const enrollmentsBreakdown = useMemo(() => {
        return report?.enrollments_breakdown || []
    }, [report])

    const breakdownColumns = useMemo(() => {
        return [
            { key: "course", label: "Corso", type: "string" },
            { key: "enrolled", label: "Iscritti", type: "number" },
        ]
    }, [])

    const breakdownRows = useMemo(() => {
        return [...enrollmentsBreakdown]
            .sort((a, b) => normalizeName(a?.course_name).localeCompare(normalizeName(b?.course_name), "it"))
            .map((course) => ({
                id: course.course_id,
                course: course.course_name,
                enrolled: course.enrolled_students,
                editions: [...(course.editions || [])].sort((a, b) =>
                    normalizeName(a?.edition_name).localeCompare(normalizeName(b?.edition_name), "it")
                ),
            }))
    }, [enrollmentsBreakdown])

    const filteredBreakdownRows = useMemo(() => {
        const query = normalizeName(enrollmentsTableSearch)
        if (!query) {
            return breakdownRows
        }

        return breakdownRows.filter((row) => {
            if (normalizeName(row?.course).includes(query)) {
                return true
            }

            return (row?.editions || []).some((edition) => normalizeName(edition?.edition_name).includes(query))
        })
    }, [breakdownRows, enrollmentsTableSearch])

    const displayedCourseOptions = useMemo(() => {
        const query = normalizeName(courseSearch)
        if (!query) {
            return options.courses
        }

        return options.courses.filter((item) => normalizeName(item?.name).includes(query))
    }, [options.courses, courseSearch])

    const displayedEditionOptions = useMemo(() => {
        const query = normalizeName(editionSearch)
        if (!query) {
            return options.editions
        }

        return options.editions.filter((item) => normalizeName(item?.name).includes(query))
    }, [options.editions, editionSearch])

    const learningMetricCards = useMemo(() => {
        const metrics = report?.learning_metrics || {}
        return [
            {
                key: "avg_course_completion_rate",
                label: "Completamento Corsi",
                value: formatPercent(metrics.avg_course_completion_rate),
            },
            {
                key: "students_following_live_rate",
                label: "Frequenza Live",
                value: formatPercent(metrics.students_following_live_rate),
            },
            {
                key: "students_following_recorded_rate",
                label: "Frequenza Rec",
                value: formatPercent(metrics.students_following_recorded_rate),
            },
            {
                key: "avg_live_completion_rate",
                label: "Completamento Live",
                value: formatPercent(metrics.avg_live_completion_rate),
            },
            {
                key: "avg_recorded_completion_rate",
                label: "Completamento Rec",
                value: formatPercent(metrics.avg_recorded_completion_rate),
            },
        ]
    }, [report])

    const teachersHoursColumns = useMemo(() => {
        return [
            { key: "teacher", label: "Docente", type: "string" },
            { key: "hours", label: "Ore svolte", type: "custom" },
        ]
    }, [])

    const teachersHoursRows = useMemo(() => {
        return [...(report?.teachers_hours || [])]
            .sort((a, b) => normalizeName(a?.teacher_name).localeCompare(normalizeName(b?.teacher_name), "it"))
            .map((teacher) => ({
                id: teacher.teacher_id,
                teacher: teacher.teacher_name,
                hours: {
                    type: "number",
                    value: Math.max(Math.round(Number(teacher.total_hours || 0) * 60), 0),
                    component: formatDurationHm(Math.max(Math.round(Number(teacher.total_hours || 0) * 60), 0)),
                },
                breakdown: teacher.breakdown || [],
            }))
    }, [report])

    const filteredTeachersHoursRows = useMemo(() => {
        const query = normalizeName(teachersTableSearch)
        if (!query) {
            return teachersHoursRows
        }

        return teachersHoursRows.filter((row) => {
            if (normalizeName(row?.teacher).includes(query)) {
                return true
            }

            return (row?.breakdown || []).some((item) => {
                return normalizeName(item?.course_name).includes(query) || normalizeName(item?.edition_name).includes(query)
            })
        })
    }, [teachersHoursRows, teachersTableSearch])

    const segmentTotal = segmentChartData.reduce((sum, item) => sum + item.value, 0)
    const typeTotal = typeChartData.reduce((sum, item) => sum + item.value, 0)
    const metrics = report?.learning_metrics || {}
    const renderLearning = (cards) => <div className={styles.learningBars}>{cards.map(card => <div key={card.key} className={styles.learningRow}>
        <div><span>{card.label.replace("Rec", "registrazioni")}</span><strong>{loading.reportLearning ? "…" : errors.reportLearning ? "—" : card.value}</strong></div>
        <div className={styles.track}><span style={{ width: `${Math.min(100, Math.max(0, Number(metrics[card.key] || 0)))}%` }} /></div>
    </div>)}</div>

    return (
        <HeaderFooterLayout>
        <HelmetProvider><Helmet><title>Report - Didattica</title></Helmet></HelmetProvider>
        <div className={styles.page}>
        <div className={styles.container}>
            <Breadcrumb items={[{ label: "Report" }, { label: "Didattica" }]} />
            <header className={styles.heading}><h1>Report didattica</h1><p>Iscrizioni, partecipazione e progressi degli studenti, in un unico sguardo.</p></header>
            <div className={styles.filters}>
                <div><label>Corsi</label><MultipleSelect appearance="modern" portal selected={filters.courses} options={displayedCourseOptions}
                    placeholder={loading.courses ? "Caricamento…" : "Tutti i corsi"} searchPlaceholder="Cerca corso" onSearch={value => setCourseSearch(value ?? "")}
                    onSelect={courses => setFilters({ courses, editions: [] })} disabled={loading.courses} /></div>
                <div><label>Edizioni</label><MultipleSelect appearance="modern" portal selected={filters.editions} options={displayedEditionOptions}
                    placeholder={loading.editions ? "Caricamento…" : "Tutte le edizioni"} searchPlaceholder="Cerca edizione" onSearch={value => setEditionSearch(value ?? "")}
                    onSelect={editions => setFilters(current => ({ ...current, editions }))} disabled={!filters.courses.length || loading.editions} /></div>
                <p>I dati si aggiornano in base ai corsi e alle edizioni selezionati.</p>
            </div>
            {Object.values(errors).some(Boolean) && <div role="alert" className={styles.error}>Alcune sezioni non sono disponibili. Ricarica la pagina per riprovare.</div>}
            <div className={styles.kpiGrid}>
                {[
                    ["Studenti iscritti", number(report.enrolled_students), "Studenti unici nella selezione", "reportSummary"],
                    ["Studenti attivi", number(report.student_segments?.active), `${formatPercent(segmentTotal ? report.student_segments?.active / segmentTotal * 100 : 0)} degli studenti classificati`, "reportSummary"],
                    ["Completamento corsi", formatPercent(metrics.avg_course_completion_rate), "Avanzamento medio dei corsi", "reportLearning"],
                    ["Ore di docenza", formatDurationHm(teachersHoursRows.reduce((sum, row) => sum + row.hours.value, 0)), "Totale ore svolte dai docenti", "reportTeachers"],
                ].map(([label, value, hint, key]) => <Card appearance="modern" key={label}><div className={styles.kpiLabel}>{label}</div><div className={styles.kpiValue}>{loading[key] ? <Skeleton width={100} height={32} /> : errors[key] ? "—" : value}</div><div className={styles.hint}>{loading[key] || errors[key] ? "Dati in aggiornamento" : hint}</div></Card>)}
            </div>
            <div className={styles.mainGrid}>
                <Card appearance="modern" title="Andamento delle iscrizioni" actions={<div className={styles.toggle} aria-label="Visualizzazione iscrizioni">{[["count", "Giornaliere"], ["cumulative", "Cumulative"]].map(([key, label]) => <button type="button" key={key} aria-pressed={trendMode === key} onClick={() => setTrendMode(key)}>{label}</button>)}</div>}>
                    <p className={styles.hint}>Iscrizioni ai corsi: uno studente può avere più iscrizioni.</p>
                    <div className={styles.trendChart}>
                        {loading.reportSummary ? <Skeleton width="100%" height={260} /> : !trendData.length ? <div className={styles.empty}>Nessun dato disponibile</div> : <ResponsiveContainer width="100%" height="100%"><ComposedChart data={trendData} margin={{ top: 20, right: 14, bottom: 0, left: 0 }}>
                            <defs><linearGradient id="teachingTrend" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#26aa87" stopOpacity={0.24} /><stop offset="100%" stopColor="#26aa87" stopOpacity={0.01} /></linearGradient></defs>
                            <CartesianGrid vertical={false} stroke="rgba(0,0,0,.06)" strokeDasharray="3 3" />
                            <XAxis dataKey="date" tickFormatter={dateLabel} minTickGap={42} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} width={44} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                            <Tooltip labelFormatter={dateLabel} contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} formatter={value => [number(value), trendMode === "count" ? "Iscrizioni del giorno" : "Iscrizioni cumulative"]} />
                            {trendMode === "count" ? <Bar dataKey="count" fill="#26aa87" maxBarSize={24} radius={[3, 3, 0, 0]} /> : <Area type="monotone" dataKey="cumulative" stroke="#26aa87" strokeWidth={2.5} fill="url(#teachingTrend)" />}
                        </ComposedChart></ResponsiveContainer>}
                    </div>
                </Card>
                <Card appearance="modern" title="Attività degli studenti">
                    <p className={styles.hint}>Classificazione basata su avanzamento, visione e giorni di attività.</p>
                    {loading.reportSummary ? <Skeleton width="100%" height={260} /> : !segmentTotal ? <div className={styles.empty}>Nessun dato disponibile</div> : <>
                        <div className={styles.donut}><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={segmentChartData} dataKey="value" innerRadius={65} outerRadius={86} paddingAngle={3} stroke="none">{segmentChartData.map((item, index) => <Cell key={item.name} fill={PIE_COLORS[index]} />)}</Pie><Tooltip formatter={value => [number(value), "Studenti"]} /></PieChart></ResponsiveContainer><div className={styles.donutCenter}><strong>{number(segmentTotal)}</strong><span>studenti</span></div></div>
                        <div className={styles.legend}>{segmentChartData.map((item, index) => <div key={item.name}><span><i style={{ background: PIE_COLORS[index] }} />{item.name}</span><strong>{number(item.value)} <small>{formatPercent(item.value / segmentTotal * 100)}</small></strong></div>)}</div>
                    </>}
                    <details className={styles.segmentHelp}>
                        <summary>Cosa significano queste categorie?</summary>
                        <dl>
                            <div><dt>Attivi · punteggio ≥ 66%</dt><dd>Coinvolgimento alto, considerando avanzamento, visione delle lezioni e giorni di attività.</dd></div>
                            <div><dt>Passivi · punteggio ≥ 33% e &lt; 66%</dt><dd>Coinvolgimento intermedio: partecipazione e progressi ancora parziali.</dd></div>
                            <div><dt>A rischio · punteggio &lt; 33%</dt><dd>Coinvolgimento basso o assente nei dati disponibili: studenti da monitorare.</dd></div>
                        </dl>
                        <p>Il punteggio combina avanzamento medio nelle edizioni (50%), quota media di lezione vista (30%) e giorni distinti di attività (20%, con il massimo raggiunto a 8 giorni).</p>
                        <p>La classificazione riguarda i corsi e le edizioni selezionati. “A rischio” segnala un basso coinvolgimento, non un abbandono accertato; anche chi ha appena iniziato può rientrare in questa categoria.</p>
                    </details>
                </Card>
            </div>
            <div className={styles.threeGrid}>
                <Card appearance="modern" title="Studenti per tipologia"><p className={styles.hint}>Quote sul totale delle presenze nelle tipologie: uno studente può comparire più volte.</p><Distribution loading={loading.reportSummary} items={typeChartData.map(item => ({ label: item.name, count: item.value, percentage: typeTotal ? item.value / typeTotal * 100 : 0 }))} /></Card>
                <Card appearance="modern" title="Progressi di apprendimento"><p className={styles.hint}>Percentuale media di completamento.</p>{renderLearning([learningMetricCards[0], ...learningMetricCards.slice(3)])}</Card>
                <Card appearance="modern" title="Modalità di frequenza"><p className={styles.hint}>Gli studenti possono seguire sia live sia registrazioni.</p>{renderLearning(learningMetricCards.slice(1, 3))}</Card>
            </div>
            <div className={styles.sectionHeading}><h2>Profilo degli studenti</h2><span>{loading.reportSummary ? "Caricamento…" : `${number(studentDemographicsSummary.students_total)} studenti`}</span></div>
            <div className={styles.threeGrid}>
                <Card appearance="modern" title="Fasce d’età"><p className={styles.hint}>Età media: <strong>{studentDemographicsSummary.avg_age != null ? `${Number(studentDemographicsSummary.avg_age).toFixed(1)} anni` : "non disponibile"}</strong></p><Distribution loading={loading.reportSummary} items={ageDistributionChartData} /></Card>
                <Card appearance="modern" title="Genere"><Distribution loading={loading.reportSummary} items={genderDistributionChartData} /></Card>
                <Card appearance="modern" title="Occupazione"><Distribution loading={loading.reportSummary} items={employmentDistributionChartData} /></Card>
            </div>
            <div className={styles.sectionHeading}><h2>Dettaglio didattico</h2><span>Corsi, edizioni e attività dei docenti</span></div>
            <div className={styles.tablesGrid}>
                    <Card appearance="modern" integratedTable title="Iscritti per corso">
                        <p className={styles.tableHint}>Seleziona una riga per visualizzare il dettaglio delle edizioni.</p>
                        <TextInput appearance="modern"
                            type="search"
                            placeholder="Cerca corso o edizione"
                            onKeyUp={(value) => {
                                setEnrollmentsTableSearch(value ?? "")
                            }}
                        />
                        <Table
                            loading={loading.reportBreakdown}
                            loadingN={5}
                            appearance="minimal"
                            columns={breakdownColumns}
                            elements={filteredBreakdownRows}
                            defaultSort={{ key: "course", direction: "asc" }}
                            style={{ maxHeight: 500 }}
                            onRowClick={(row) => {
                                setExpandedCourseIds((current) => {
                                    if (current.includes(row.id)) {
                                        return current.filter((id) => id !== row.id)
                                    }
                                    return [...current, row.id]
                                })
                            }}
                            expandedRowIds={expandedCourseIds}
                            renderExpandedRow={(row) => (
                                <div className={styles.editionBreakdownWrap}>
                                    <table className={styles.editionBreakdownTable}>
                                        <thead>
                                            <tr>
                                                <th>Edizione</th>
                                                <th>Iscritti</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(row.editions || []).map((edition, index) => (
                                                <tr key={`${row.id}-edition-${edition.edition_id ?? "next"}-${index}`}>
                                                    <td>{edition.edition_name}</td>
                                                    <td>{edition.enrolled_students}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        />
                    </Card>

                    <Card appearance="modern" integratedTable title="Ore docenti">
                        <p className={styles.tableHint}>Seleziona una riga per visualizzare il dettaglio delle edizioni.</p>
                        <TextInput appearance="modern"
                            type="search"
                            placeholder="Cerca docente, corso o edizione"
                            onKeyUp={(value) => {
                                setTeachersTableSearch(value ?? "")
                            }}
                        />
                        <Table
                            loading={loading.reportTeachers}
                            loadingN={5}
                            appearance="minimal"
                            columns={teachersHoursColumns}
                            elements={filteredTeachersHoursRows}
                            style={{ maxHeight: 500 }}
                            defaultSort={{ key: "teacher", direction: "asc" }}
                            onRowClick={(row) => {
                                setExpandedTeacherIds((current) => {
                                    if (current.includes(row.id)) {
                                        return current.filter((id) => id !== row.id)
                                    }
                                    return [...current, row.id]
                                })
                            }}
                            expandedRowIds={expandedTeacherIds}
                            renderExpandedRow={(row) => (
                                <div className={styles.editionBreakdownWrap}>
                                    <table className={styles.editionBreakdownTable}>
                                        <thead>
                                            <tr>
                                                <th>Corso</th>
                                                <th>Edizione</th>
                                                <th>Ore</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(row.breakdown || []).map((item, index) => (
                                                <tr key={`${row.id}-breakdown-${item.course_id ?? "na"}-${item.edition_id ?? "na"}-${index}`}>
                                                    <td>{item.course_name}</td>
                                                    <td>{item.edition_name}</td>
                                                    <td>{formatDurationHm(Math.max(Math.round(Number(item.hours || 0) * 60), 0))}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        />
                    </Card>
            </div>
        </div>
        </div>
        </HeaderFooterLayout>
    )
}

export default TeachingReports
